import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, ExternalLink, MoreHorizontal, Archive, ArchiveRestore, Trash2, Pencil } from "lucide-react";

const STATUS_TABS = [
  { key: "active", label: "Active" },
  { key: "archived", label: "Archived" },
  { key: "all", label: "All" },
];

const EMPTY_FORM = {
  name: "", legal_name: "", industry: "", country: "IN", currency: "INR",
  billing_model: "per_project", hourly_rate: "", notes: "",
};

function toPayload(form) {
  const p = { ...form };
  p.hourly_rate = p.hourly_rate ? Number(p.hourly_rate) : null;
  if (!p.industry) delete p.industry;
  if (!p.legal_name) delete p.legal_name;
  if (!p.notes) delete p.notes;
  return p;
}

function ClientForm({ form, setForm }) {
  return (
    <div className="mt-6 space-y-4">
      <div>
        <Label htmlFor="c-name">Client name</Label>
        <Input id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="client-name" />
      </div>
      <div>
        <Label htmlFor="c-legal">Legal name</Label>
        <Input id="c-legal" value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="client-legal" />
      </div>
      <div>
        <Label htmlFor="c-industry">Industry</Label>
        <Input id="c-industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} data-testid="client-industry" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Billing model</Label>
          <Select value={form.billing_model} onValueChange={(v) => setForm({ ...form, billing_model: v })}>
            <SelectTrigger data-testid="client-billing"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="per_project">Per Project</SelectItem>
              <SelectItem value="monthly_hours">Monthly Hours</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="c-rate">Hourly Rate</Label>
          <Input id="c-rate" type="number" value={form.hourly_rate || ""} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} disabled={form.billing_model !== "monthly_hours"} data-testid="client-rate" />
        </div>
      </div>
      <div>
        <Label htmlFor="c-notes">Notes</Label>
        <Input id="c-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="client-notes" />
      </div>
    </div>
  );
}

function DeleteButton({ client, onDelete }) {
  const [open, setOpen] = useState(false);
  const { data: linked } = useQuery({
    queryKey: ["client-linked", client.id, open],
    queryFn: async () => (await api.get(`/clients/${client.id}/linked`)).data,
    enabled: open,
  });
  const canDelete = linked?.can_delete;
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <button className="w-full text-left px-2 py-1.5 text-sm hover:bg-zinc-100 rounded-sm flex items-center gap-2 text-red-600" data-testid={`delete-${client.id}`}>
          <Trash2 className="w-3.5 h-3.5" /> Delete permanently
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {client.name}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div>
              {linked ? (
                canDelete ? (
                  <>This client has no linked business records. Deletion is permanent and cannot be undone.</>
                ) : (
                  <>
                    <div>This client cannot be deleted because it has linked records:</div>
                    <ul className="mt-3 space-y-1 text-xs font-mono text-zinc-700">
                      {Object.entries(linked).filter(([k, v]) => !["total", "can_delete"].includes(k) && v > 0).map(([k, v]) => (
                        <li key={k}>· {k}: <span className="font-bold">{v}</span></li>
                      ))}
                    </ul>
                    <div className="mt-3 text-sm">Archive it instead — this preserves history while removing it from active views.</div>
                  </>
                )
              ) : "Checking linked records…"}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`delete-cancel-${client.id}`}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={!canDelete}
            onClick={() => { onDelete(client.id); setOpen(false); }}
            className="bg-red-600 hover:bg-red-700"
            data-testid={`delete-confirm-${client.id}`}
          >Delete permanently</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function Clients() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("active");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const { data: clients = [] } = useQuery({
    queryKey: ["clients", tab],
    queryFn: async () => (await api.get(`/clients?status=${tab}`)).data,
  });

  const counts = useMemo(() => ({
    active: clients.filter((c) => c.status !== "archived").length,
    archived: clients.filter((c) => c.status === "archived").length,
  }), [clients]);

  const createMut = useMutation({
    mutationFn: (p) => api.post("/clients", p),
    onSuccess: () => {
      toast.success("Client added");
      setAddOpen(false);
      setForm(EMPTY_FORM);
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["kpis"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, payload }) => api.patch(`/clients/${id}`, payload),
    onSuccess: () => {
      toast.success("Client updated");
      setEditOpen(false);
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["clients"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const archiveMut = useMutation({
    mutationFn: (id) => api.post(`/clients/${id}/archive`),
    onSuccess: () => {
      toast.success("Client archived");
      qc.invalidateQueries({ queryKey: ["clients"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const unarchiveMut = useMutation({
    mutationFn: (id) => api.post(`/clients/${id}/unarchive`),
    onSuccess: () => {
      toast.success("Client restored");
      qc.invalidateQueries({ queryKey: ["clients"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/clients/${id}`),
    onSuccess: () => {
      toast.success("Client deleted");
      qc.invalidateQueries({ queryKey: ["clients"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const submitCreate = (e) => { e.preventDefault(); createMut.mutate(toPayload(form)); };
  const submitEdit = (e) => { e.preventDefault(); updateMut.mutate({ id: editing.id, payload: toPayload(form) }); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", legal_name: c.legal_name || "", industry: c.industry || "",
      country: c.country || "IN", currency: c.currency || "INR",
      billing_model: c.billing_model || "per_project", hourly_rate: c.hourly_rate ?? "", notes: c.notes || "",
    });
    setEditOpen(true);
  };

  return (
    <div data-testid="clients-page">
      <PageHeader
        eyebrow="Business Division · CRM"
        title="Clients"
        description="The heart of the platform. Every project, quotation and invoice rolls up to a client relationship."
        actions={
          <Sheet open={addOpen} onOpenChange={setAddOpen}>
            <SheetTrigger asChild>
              <Button data-testid="new-client-btn"><Plus className="w-4 h-4 mr-1.5" /> New Client</Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md">
              <SheetHeader><SheetTitle>Add a client</SheetTitle></SheetHeader>
              <form onSubmit={submitCreate} data-testid="new-client-form">
                <ClientForm form={form} setForm={setForm} />
                <SheetFooter className="mt-6">
                  <Button type="submit" disabled={createMut.isPending} data-testid="client-submit">Add Client</Button>
                </SheetFooter>
              </form>
            </SheetContent>
          </Sheet>
        }
      />

      <div className="max-w-[1400px] mx-auto px-8 py-6 space-y-4">
        {/* Status tabs */}
        <div className="flex items-center gap-1 border-b border-zinc-200" data-testid="clients-status-tabs">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === t.key ? "border-zinc-900 text-zinc-900 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
              {t.key !== "all" && (
                <span className="ml-2 text-[11px] font-mono text-zinc-400">
                  {t.key === "active" ? counts.active : counts.archived}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden" data-testid="clients-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Industry</th>
                <th className="px-4 py-3">Billing</th>
                <th className="px-4 py-3">Rate</th>
                <th className="px-4 py-3">Country</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {clients.map((c) => {
                const archived = c.status === "archived";
                return (
                  <tr key={c.id} data-testid={`client-row-${c.id}`} className={archived ? "opacity-60" : ""}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-zinc-900">{c.name}</div>
                      <div className="text-[11px] text-zinc-500">{c.legal_name || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-600">{c.industry || "—"}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{c.billing_model.replace("_", " ")}</Badge></td>
                    <td className="px-4 py-3 font-mono text-xs">{c.hourly_rate ? `₹${c.hourly_rate}/hr` : "—"}</td>
                    <td className="px-4 py-3">{c.country}</td>
                    <td className="px-4 py-3">
                      {archived
                        ? <Badge variant="secondary" className="bg-zinc-100 text-zinc-600" data-testid={`status-${c.id}`}>Archived</Badge>
                        : <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`status-${c.id}`}>Active</Badge>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Link to={`/app/clients/${c.id}`} className="text-xs text-zinc-700 hover:text-zinc-900 inline-flex items-center gap-1" data-testid={`client-open-${c.id}`}>
                          Open <ExternalLink className="w-3 h-3" />
                        </Link>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1.5 rounded-sm hover:bg-zinc-100" data-testid={`row-menu-${c.id}`} aria-label="Row actions">
                              <MoreHorizontal className="w-4 h-4 text-zinc-500" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuItem onSelect={() => openEdit(c)} data-testid={`edit-${c.id}`}>
                              <Pencil className="w-3.5 h-3.5 mr-2" /> Edit details
                            </DropdownMenuItem>
                            {archived ? (
                              <DropdownMenuItem onSelect={() => unarchiveMut.mutate(c.id)} data-testid={`unarchive-${c.id}`}>
                                <ArchiveRestore className="w-3.5 h-3.5 mr-2" /> Restore
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onSelect={() => archiveMut.mutate(c.id)} data-testid={`archive-${c.id}`}>
                                <Archive className="w-3.5 h-3.5 mr-2" /> Archive
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <div className="p-0">
                              <DeleteButton client={c} onDelete={(id) => deleteMut.mutate(id)} />
                            </div>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {clients.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-zinc-500" data-testid="clients-empty">
                  {tab === "archived" ? "No archived clients." : tab === "active" ? "No active clients yet." : "No clients yet."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit sheet */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader><SheetTitle>Edit {editing?.name}</SheetTitle></SheetHeader>
          <form onSubmit={submitEdit} data-testid="edit-client-form">
            <ClientForm form={form} setForm={setForm} />
            <SheetFooter className="mt-6">
              <Button type="submit" disabled={updateMut.isPending} data-testid="edit-submit">Save changes</Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
